import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  Send, 
  Pencil,
  Copy,
  Check,
  ArrowLeft,
  Save,
  X
} from "lucide-react";
import { CoverBlock } from "@/components/proposals/blocks/CoverBlock";
import { EditableCoverBlock } from "@/components/proposals/blocks/EditableCoverBlock";
import { TextBlock } from "@/components/proposals/blocks/TextBlock";
import { ImageBlock } from "@/components/proposals/blocks/ImageBlock";
import { LineItemsTable } from "@/components/proposals/blocks/LineItemsTable";
import { SignatureBlock } from "@/components/proposals/blocks/SignatureBlock";
import { NeighborhoodMapBlock } from "@/components/proposals/blocks/NeighborhoodMapBlock";
import type { ProposalDocument, Block } from "@/types/proposals";
import { toast } from "sonner";

export default function ProposalReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<ProposalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedLayout, setEditedLayout] = useState<any>(null);

  useEffect(() => {
    if (id) {
      loadProposal();
    }
  }, [id]);

  const loadProposal = async () => {
    try {
      const { data, error } = await supabase
        .from("proposal_documents")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      setProposal(data as unknown as ProposalDocument);
    } catch (error) {
      console.error("Error loading proposal:", error);
      toast.error("Failed to load proposal");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyShareLink = () => {
    if (!proposal?.public_share_id) return;
    
    const shareUrl = `${window.location.origin}/proposal/${proposal.public_share_id}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Share link copied to clipboard!");
    
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendToCustomer = async () => {
    if (!proposal) return;
    
    // Update status to sent
    try {
      await supabase
        .from("proposal_documents")
        .update({ status: "sent" })
        .eq("id", proposal.id);
      
      toast.success("Proposal marked as sent!");
      loadProposal();
    } catch (error) {
      console.error("Error updating proposal status:", error);
      toast.error("Failed to update proposal status");
    }
  };

  const handleDownloadPDF = () => {
    toast.info("PDF download feature coming soon!");
  };

  const handleStartEdit = () => {
    setEditedLayout(JSON.parse(JSON.stringify(proposal?.layout_filled_json)));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (confirm("Discard all changes? This cannot be undone.")) {
      setIsEditing(false);
      setEditedLayout(null);
    }
  };

  const handleBackNavigation = () => {
    if (isEditing) {
      if (confirm("You have unsaved changes. Save before leaving?")) {
        handleSaveChanges().then(() => navigate(-1));
      } else {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };
  const handleSaveChanges = async () => {
    if (!proposal || !editedLayout) return;

    try {
      const { error } = await supabase
        .from("proposal_documents")
        .update({ layout_filled_json: editedLayout })
        .eq("id", proposal.id);

      if (error) throw error;

      toast.success("Changes saved successfully!");
      setIsEditing(false);
      await loadProposal();
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes");
    }
  };

  const handleBlockUpdate = (blockId: string, updates: any) => {
    if (!editedLayout) return;

    const updatedLayout = { ...editedLayout };
    updatedLayout.pages = updatedLayout.pages.map((page: any) => ({
      ...page,
      blocks: page.blocks.map((block: any) =>
        block.id === blockId ? { ...block, content: updates } : block
      ),
    }));

    setEditedLayout(updatedLayout);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "signed":
        return "bg-success text-success-foreground";
      case "sent":
        return "bg-info text-info-foreground";
      case "viewed":
        return "bg-warning text-warning-foreground";
      default:
        return "bg-secondary text-secondary-foreground";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading proposal...</p>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-12 text-center">
          <p className="text-lg">Proposal not found</p>
        </Card>
      </div>
    );
  }

  const layoutToRender = isEditing && editedLayout ? editedLayout : proposal.layout_filled_json;
  const pages = layoutToRender.pages;
  const currentPage = pages[currentPageIndex];
  const isSigned = proposal.status === "signed";

  const renderBlock = (block: Block) => {
    const props = {
      block,
      onUpdate: () => {},
      onSelect: () => {},
    };

    switch (block.type) {
      case "cover":
        if (isEditing) {
          return (
            <EditableCoverBlock
              key={block.id}
              block={block}
              isEditing={isEditing}
              onUpdate={(updates) => handleBlockUpdate(block.id, updates)}
            />
          );
        }
        return <CoverBlock key={block.id} {...props} />;
      case "text":
        return <TextBlock key={block.id} {...props} />;
      case "image":
        return <ImageBlock key={block.id} {...props} />;
      case "line_items_table":
        return <LineItemsTable key={block.id} {...props} />;
      case "signature":
        return <SignatureBlock key={block.id} {...props} />;
      case "neighborhood_map":
        return <NeighborhoodMapBlock key={block.id} block={block} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="border-b bg-card px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackNavigation}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex items-center gap-4 flex-1">
              <h1 className="text-2xl font-bold">{proposal.title}</h1>
              <Badge className={getStatusColor(proposal.status)}>
                {proposal.status}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button 
                    size="sm"
                    onClick={handleSaveChanges}
                    className="gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleCancelEdit}
                    className="gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleStartEdit}
                    className="gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    Quick Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDownloadPDF}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </Button>
                </>
              )}
            </div>
          </div>
          
          {/* Share and Send Actions */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyShareLink}
              disabled={!proposal.public_share_id}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Share Link
                </>
              )}
            </Button>
            {proposal.status === "draft" && (
              <Button
                size="sm"
                onClick={handleSendToCustomer}
                className="bg-action hover:bg-action-hover"
              >
                <Send className="w-4 h-4 mr-2" />
                Mark as Sent
              </Button>
            )}
            {isSigned && (
              <p className="text-sm text-success">
                ✓ Signed by {proposal.signed_by_name}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="bg-white dark:bg-card shadow-lg p-12 min-h-[800px]">
            {currentPage && (
              <div className="space-y-6">
                {currentPage.blocks
                  .sort((a, b) => a.order - b.order)
                  .map((block) => renderBlock(block))}
              </div>
            )}
          </Card>

          {/* Page Navigation */}
          {pages.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrentPageIndex(Math.max(0, currentPageIndex - 1))}
                disabled={currentPageIndex === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPageIndex + 1} of {pages.length}
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPageIndex(Math.min(pages.length - 1, currentPageIndex + 1))}
                disabled={currentPageIndex === pages.length - 1}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
